import {
  FileAttachment_Read_Nested,
  Timesheet_Driver_Day_Summary,
  Timesheet_List_Read,
  Timesheet_Metadata_Read,
  TimesheetSource,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { JobBasic } from '~hooks/useJob/models';
import { Nullable } from '~types/Nullable';

class TimesheetDriverDayDriver {
  constructor(
    private _id: string,
    private _first_name: Nullable<string>,
    private _last_name: Nullable<string>,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }
}

class TimesheetDriverDaySummary {
  constructor(
    private _id: string,
    private _driver_day_id: string,
    private _driver_day_date: Dayjs,
    private _started_shift_at: Nullable<Dayjs>,
    private _ended_shift_at: Nullable<Dayjs>,
    private _driver: Nullable<TimesheetDriverDayDriver>,
  ) {}

  public get id() {
    return this._id;
  }

  public get driverDayId() {
    return this._driver_day_id;
  }

  public get driverDayDate() {
    return this._driver_day_date;
  }

  public get startedShiftAt() {
    return this._started_shift_at;
  }

  public get endedShiftAt() {
    return this._ended_shift_at;
  }

  public get driver() {
    return this._driver;
  }

  public static parse(proto: Timesheet_Driver_Day_Summary) {
    return new TimesheetDriverDaySummary(
      proto.id,
      proto.driver_day_id,
      dayjs(proto.driver_day_date),
      proto.started_shift_at ? dayjs(proto.started_shift_at) : null,
      proto.ended_shift_at ? dayjs(proto.ended_shift_at) : null,
      proto.driver
        ? new TimesheetDriverDayDriver(
            proto.driver.id,
            proto.driver.first_name ?? null,
            proto.driver.last_name ?? null,
          )
        : null,
    );
  }
}

class FileAttachmentBasic {
  constructor(
    private _id: string,
    private _file_name: string,
    private _file_url: string,
    private _mime_type: string,
    private _preview_url: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get fileName() {
    return this._file_name;
  }

  public get fileUrl() {
    return this._file_url;
  }

  public get mimeType() {
    return this._mime_type;
  }

  public get previewUrl() {
    return this._preview_url;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: FileAttachment_Read_Nested) {
    return new FileAttachmentBasic(
      proto.id,
      proto.file_name,
      proto.file_url,
      proto.mime_type,
      proto.preview_url ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class Timesheet {
  constructor(
    private _id: string,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _company_id: string,
    private _source: TimesheetSource,
    private _timesheet_number: Nullable<string>,
    private _shift_date: Nullable<Dayjs>,
    private _started_at: Nullable<Dayjs>,
    private _ended_at: Nullable<Dayjs>,
    private _total_minutes: Nullable<number>,
    private _net_minutes: Nullable<number>,
    private _break_minutes: Nullable<number>,
    private _extracted_number: Nullable<string>,
    private _extracted_date: Nullable<Dayjs>,
    private _extracted_start_at: Nullable<Dayjs>,
    private _extracted_end_at: Nullable<Dayjs>,
    private _extracted_total_minutes: Nullable<number>,
    private _extracted_net_minutes: Nullable<number>,
    private _extracted_break_minutes: Nullable<number>,
    private _ai_processed: boolean,
    private _ai_confidence_score: Nullable<string>,
    private _job_id: Nullable<string>,
    private _driver_day_id: Nullable<string>,
    private _job: Nullable<JobBasic>,
    private _driver_day: Nullable<TimesheetDriverDaySummary>,
    private _file_attachment: Nullable<FileAttachmentBasic>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get companyId() {
    return this._company_id;
  }

  public get source() {
    return this._source;
  }

  public get timesheetNumber() {
    return this._timesheet_number;
  }

  public get shiftDate() {
    return this._shift_date;
  }

  public get startedAt() {
    return this._started_at;
  }

  public get endedAt() {
    return this._ended_at;
  }

  public get totalMinutes() {
    return this._total_minutes;
  }

  public get netMinutes() {
    return this._net_minutes;
  }

  public get breakMinutes() {
    return this._break_minutes;
  }

  public get extractedNumber() {
    return this._extracted_number;
  }

  public get extractedDate() {
    return this._extracted_date;
  }

  public get extractedStartAt() {
    return this._extracted_start_at;
  }

  public get extractedEndAt() {
    return this._extracted_end_at;
  }

  public get extractedTotalMinutes() {
    return this._extracted_total_minutes;
  }

  public get extractedNetMinutes() {
    return this._extracted_net_minutes;
  }

  public get extractedBreakMinutes() {
    return this._extracted_break_minutes;
  }

  public get aiProcessed() {
    return this._ai_processed;
  }

  public get aiConfidenceScore() {
    return this._ai_confidence_score;
  }

  public get jobId() {
    return this._job_id;
  }

  public get driverDayId() {
    return this._driver_day_id;
  }

  public get job() {
    return this._job;
  }

  public get driverDay() {
    return this._driver_day;
  }

  public get fileAttachment() {
    return this._file_attachment;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  // Update / image responses are Timesheet_Read, which omits the job / driver_day summaries that
  // only the list endpoint returns. When refreshing an existing row from such a response, carry
  // the previously-loaded summaries forward so the inbox keeps its "Linked to" data.
  public withLinkedSummariesFrom(previous: Timesheet | null) {
    if (!previous) {
      return this;
    }

    return new Timesheet(
      this._id,
      this._created_at,
      this._updated_at,
      this._company_id,
      this._source,
      this._timesheet_number,
      this._shift_date,
      this._started_at,
      this._ended_at,
      this._total_minutes,
      this._net_minutes,
      this._break_minutes,
      this._extracted_number,
      this._extracted_date,
      this._extracted_start_at,
      this._extracted_end_at,
      this._extracted_total_minutes,
      this._extracted_net_minutes,
      this._extracted_break_minutes,
      this._ai_processed,
      this._ai_confidence_score,
      this._job_id,
      this._driver_day_id,
      this._job ?? previous._job,
      this._driver_day ?? previous._driver_day,
      this._file_attachment,
      this._discarded_at,
    );
  }

  public static parse(proto: Timesheet_List_Read) {
    return new Timesheet(
      proto.id,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.company_id,
      proto.source,
      proto.timesheet_number ?? null,
      proto.shift_date ? dayjs(proto.shift_date) : null,
      proto.started_at ? dayjs(proto.started_at) : null,
      proto.ended_at ? dayjs(proto.ended_at) : null,
      proto.total_minutes ?? null,
      proto.net_minutes ?? null,
      proto.break_minutes ?? null,
      proto.extracted_number ?? null,
      proto.extracted_date ? dayjs(proto.extracted_date) : null,
      proto.extracted_start_at ? dayjs(proto.extracted_start_at) : null,
      proto.extracted_end_at ? dayjs(proto.extracted_end_at) : null,
      proto.extracted_total_minutes ?? null,
      proto.extracted_net_minutes ?? null,
      proto.extracted_break_minutes ?? null,
      proto.ai_processed,
      proto.ai_confidence_score ?? null,
      proto.job_id ?? null,
      proto.driver_day_id ?? null,
      proto.job ? JobBasic.parse(proto.job) : null,
      proto.driver_day ? TimesheetDriverDaySummary.parse(proto.driver_day) : null,
      proto.file_attachment ? FileAttachmentBasic.parse(proto.file_attachment) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class TimesheetMetadata {
  constructor(
    private _id: string,
    private _ai_extracted_full_text: Nullable<string>,
    private _ai_structured_data: Nullable<Record<string, unknown>>,
    private _ai_confidence_score: Nullable<string>,
  ) {}

  public get id() {
    return this._id;
  }

  public get aiExtractedFullText() {
    return this._ai_extracted_full_text;
  }

  public get aiStructuredData() {
    return this._ai_structured_data;
  }

  public get aiConfidenceScore() {
    return this._ai_confidence_score;
  }

  public static parse(proto: Timesheet_Metadata_Read) {
    return new TimesheetMetadata(
      proto.id,
      proto.ai_extracted_full_text ?? null,
      proto.ai_structured_data ?? null,
      proto.ai_confidence_score ?? null,
    );
  }
}
